/**
 * `entities/asset-approval-chain` API surface.
 *
 * Exposes:
 *   - `getAssetApprovalChain` service fn for READING an asset's approval chain assignment.
 *   - `assetApprovalChainQuery` queryOptions helper (AD-20 — reads MUST flow through
 *     queryOptions to enforce the cache-key contract).
 *
 * Mutations (assign, remove, bulk) live in `features/assign-asset-approval-chain`
 * per AD-23 (entities expose queries; features own mutations).
 *
 * An unassigned asset is a normal state, not an error: the backend answers the
 * GET with 404, which we translate to `null` so the UI can render the "No
 * approval chain" affordance instead of surfacing an error.
 */
import { AssetWorkflowAssignmentService } from '@inference.engine/workspace-api-client/web';
import { queryOptions } from '@tanstack/react-query';
import axios from 'axios';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from './query-keys';
import type { AssetApprovalChain } from '../model/types';

// ──────────────────────────── Service functions ────────────────────────────

export const getAssetApprovalChain = async (
  projectId: string,
  assetId: string,
): Promise<AssetApprovalChain | null> => {
  try {
    const response = await AssetWorkflowAssignmentService.getProgress({
      client: workspaceHttp,
      path: { platformProjectId: projectId, assetId },
      // A 404 (no assignment) is an expected state we handle below — suppress
      // the global error toast so the user never sees it for the happy path.
      // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
      headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
    });

    return response.data ?? null;
  } catch (error) {
    // No assignment yet — the asset simply has no approval chain.
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// ──────────────────────────── queryOptions helpers ─────────────────────────

export const assetApprovalChainQuery = (
  projectId: string | undefined,
  assetId: string | undefined,
) =>
  queryOptions({
    queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId!, assetId!),
    queryFn: () => getAssetApprovalChain(projectId!, assetId!),
    enabled: !!projectId && !!assetId,
  });
